(() => {
  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const money = (amount, currency = 'GBP') => new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format((amount || 0) / 100);

  const hydrateVideo = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    if (!video.getAttribute('poster')) {
      const compact = window.matchMedia('(max-width: 620px)').matches;
      const poster = compact ? video.dataset.posterMobile : video.dataset.posterDesktop;
      const fallbackPoster = compact ? video.dataset.posterDesktop : video.dataset.posterMobile;
      if (poster || fallbackPoster) video.poster = poster || fallbackPoster;
    }
    if (video.dataset.esVideoHydrated === 'true') return;
    let attachedSource = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      attachedSource = true;
    });
    video.dataset.esVideoHydrated = 'true';
    if (attachedSource) video.load();
  };

  const initialise = (root) => {
    if (root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';

    const mainImage = root.querySelector('[data-gallery-main]');
    const galleryLabel = root.querySelector('[data-gallery-label]');
    const galleryStage = root.querySelector('[data-gallery-stage]');
    const galleryThumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const selectGalleryItem = (button, moveFocus = false) => {
        if (!mainImage) return;
        if (button.dataset.srcset) {
          mainImage.sizes = button.dataset.sizes || mainImage.sizes;
          mainImage.srcset = button.dataset.srcset;
        } else {
          mainImage.removeAttribute('srcset');
          mainImage.removeAttribute('sizes');
        }
        mainImage.src = button.dataset.src || mainImage.src;
        mainImage.alt = button.dataset.alt || mainImage.alt;
        if (galleryLabel) galleryLabel.textContent = button.dataset.label || '';
        if (galleryStage) {
          galleryStage.className = `gallery-stage ${button.dataset.kind || ''}`.trim();
          if (button.id) galleryStage.setAttribute('aria-labelledby', button.id);
        }
        galleryThumbs.forEach((thumb) => {
          const active = thumb === button;
          thumb.classList.toggle('active', active);
          thumb.setAttribute('aria-selected', String(active));
          thumb.tabIndex = active ? 0 : -1;
        });
        if (moveFocus) button.focus();
    };
    galleryThumbs.forEach((button, index) => {
      button.addEventListener('click', () => selectGalleryItem(button));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowLeft') next = (index - 1 + galleryThumbs.length) % galleryThumbs.length;
        if (event.key === 'ArrowRight') next = (index + 1) % galleryThumbs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = galleryThumbs.length - 1;
        selectGalleryItem(galleryThumbs[next], true);
      });
    });
    let galleryTouchStart = 0;
    galleryStage?.addEventListener('touchstart', (event) => { galleryTouchStart = event.changedTouches[0]?.clientX || 0; }, { passive: true });
    galleryStage?.addEventListener('touchend', (event) => {
      const distance = (event.changedTouches[0]?.clientX || 0) - galleryTouchStart;
      if (Math.abs(distance) < 45 || galleryThumbs.length < 2) return;
      const current = Math.max(0, galleryThumbs.findIndex((thumb) => thumb.classList.contains('active')));
      const next = distance < 0 ? (current + 1) % galleryThumbs.length : (current - 1 + galleryThumbs.length) % galleryThumbs.length;
      selectGalleryItem(galleryThumbs[next]);
    }, { passive: true });

    root.querySelectorAll('[data-accordion]').forEach((button) => {
      button.addEventListener('click', () => {
        const body = button.nextElementSibling;
        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!open));
        const mark = button.lastElementChild;
        if (mark) mark.textContent = open ? '+' : '−';
        if (body instanceof HTMLElement) body.hidden = open;
      });
    });

    const valueTabs = [...root.querySelectorAll('[data-precision-value]')];
    const valuePanel = root.querySelector('.precision-value__panel');
    const valueTitle = root.querySelector('[data-precision-value-title]');
    const valueCopy = root.querySelector('[data-precision-value-copy]');
    const selectValueTab = (button, moveFocus = false) => {
      if (!button || !valuePanel || !valueTitle || !valueCopy) return;
      valueTabs.forEach((tab) => {
        const active = tab === button;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      valuePanel.setAttribute('aria-labelledby', button.id);
      valueTitle.textContent = button.dataset.valueTitle || '';
      valueCopy.textContent = button.dataset.valueCopy || '';
      if (moveFocus) button.focus();
    };
    valueTabs.forEach((button, index) => {
      button.addEventListener('click', () => selectValueTab(button));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowLeft') next = (index - 1 + valueTabs.length) % valueTabs.length;
        if (event.key === 'ArrowRight') next = (index + 1) % valueTabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = valueTabs.length - 1;
        selectValueTab(valueTabs[next], true);
      });
    });

    const technique = root.querySelector('[data-technique-overlay]');
    const techniqueOpen = root.querySelector('[data-technique-open]');
    const techniqueClose = root.querySelector('[data-technique-close]');
    const drawer = root.querySelector('[data-bag-drawer]');
    const scrim = root.querySelector('[data-bag-scrim]');
    const bagClose = root.querySelector('[data-bag-close]');
    const cartBody = root.querySelector('[data-cart-body]');
    let returnFocus = null;
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const trapFocus = (container, event) => {
      const focusable = [...container.querySelectorAll(focusableSelector)].filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };

    const lockPage = (locked) => { document.body.style.overflow = locked ? 'hidden' : ''; };
    const closeTechnique = () => {
      if (!technique) return;
      technique.hidden = true;
      technique.querySelector('video')?.pause();
      techniqueOpen?.setAttribute('aria-expanded', 'false');
      lockPage(false);
      returnFocus?.focus();
    };
    const openTechnique = () => {
      if (!technique) return;
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      technique.hidden = false;
      techniqueOpen?.setAttribute('aria-expanded', 'true');
      const techniqueVideo = technique.querySelector('video');
      hydrateVideo(techniqueVideo);
      techniqueVideo?.play().catch(() => undefined);
      lockPage(true);
      techniqueClose?.focus();
    };
    techniqueOpen?.addEventListener('click', openTechnique);
    techniqueClose?.addEventListener('click', closeTechnique);
    technique?.addEventListener('click', (event) => { if (event.target === technique) closeTechnique(); });

    const renderCart = (cart) => {
      document.querySelectorAll('[data-es-cart-count], [data-bag-count]').forEach((element) => { element.textContent = `(${cart.item_count})`; });
      root.querySelectorAll('[data-bag-open]').forEach((element) => element.setAttribute('aria-label', `Open bag with ${cart.item_count} items`));
      document.querySelectorAll('.header-action--right[href*="/cart"]').forEach((element) => element.setAttribute('aria-label', `Open bag with ${cart.item_count} items`));
      if (!cartBody) return;
      if (!cart.items.length) {
        cartBody.innerHTML = '<div class="empty-bag"><p>Your Bag Is Empty.</p><button type="button" data-continue-shopping>Continue Shopping</button></div>';
        cartBody.querySelector('[data-continue-shopping]')?.addEventListener('click', closeBag);
        return;
      }

      const items = cart.items.map((item) => {
        const image = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="160" height="200">`
          : '';
        const orderType = item.properties?.['Order type'];
        const dispatch = orderType ? `<p class="bag-product-dispatch">${escapeHtml(orderType)}</p>` : '';
        return `<div class="bag-product">${image}<div><p class="bag-product-name">${escapeHtml(item.product_title)}</p>${dispatch}<p>Quantity ${item.quantity}</p><p>${money(item.final_line_price, cart.currency)}</p></div><button type="button" data-remove-key="${escapeHtml(item.key)}">Remove</button></div>`;
      }).join('');
      const hasPreOrder = cart.items.some((item) => item.properties?.['Order type'] === 'Pre-order · Dispatching in October 2026');
      const note = hasPreOrder
        ? 'Pre-order · Dispatching in October 2026'
        : 'Complimentary Tracked UK Delivery';
      cartBody.innerHTML = `${items}<div class="drawer-total"><div><span>Subtotal</span><span>${money(cart.total_price, cart.currency)}</span></div><p>${note}</p><a href="${rootPath()}checkout">Secure Checkout</a></div>`;
      cartBody.querySelectorAll('[data-remove-key]').forEach((button) => {
        button.addEventListener('click', async () => {
          button.disabled = true;
          const response = await fetch(`${rootPath()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: button.dataset.removeKey, quantity: 0 }),
          });
          if (response.ok) renderCart(await response.json());
          else button.disabled = false;
        });
      });
    };

    const refreshCart = async () => {
      const response = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load cart');
      const cart = await response.json();
      renderCart(cart);
      return cart;
    };

    function closeBag() {
      if (!drawer || !scrim) return;
      drawer.classList.remove('open');
      scrim.classList.remove('visible');
      drawer.hidden = true;
      scrim.hidden = true;
      lockPage(false);
      returnFocus?.focus();
    }
    const openBag = async ({ refresh = true } = {}) => {
      if (!drawer || !scrim) return;
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      drawer.hidden = false;
      scrim.hidden = false;
      requestAnimationFrame(() => {
        drawer.classList.add('open');
        scrim.classList.add('visible');
      });
      lockPage(true);
      bagClose?.focus();
      if (refresh) {
        try { await refreshCart(); } catch (_) { /* The drawer retains its safe loading state. */ }
      }
    };
    root.querySelectorAll('[data-bag-open]').forEach((button) => button.addEventListener('click', () => openBag()));
    bagClose?.addEventListener('click', closeBag);
    scrim?.addEventListener('click', closeBag);

    const forms = [...root.querySelectorAll('[data-product-form]')];
    const primaryForm = forms[0];
    const primaryAddButton = primaryForm?.querySelector('[data-add-button]');
    const purchaseButtons = [...root.querySelectorAll('[data-add-button], [data-add-trigger]')];
    const ensureVariantId = (form) => {
      const variantInput = form?.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement && !variantInput.value && root.dataset.variantId) {
        variantInput.value = root.dataset.variantId;
      }
      return variantInput instanceof HTMLInputElement && variantInput.value !== '';
    };

    const setPurchaseBusy = (busy) => {
      purchaseButtons.forEach((button) => {
        if (busy) {
          button.dataset.originalLabel = button.textContent || '';
          button.dataset.originalDisabled = String(button.disabled);
          button.disabled = true;
          button.textContent = 'Adding…';
        } else {
          button.disabled = button.dataset.originalDisabled === 'true';
          button.textContent = button.dataset.originalLabel || button.textContent || '';
          delete button.dataset.originalLabel;
          delete button.dataset.originalDisabled;
        }
      });
    };

    const submitProduct = async (form) => {
      if (!(form instanceof HTMLFormElement) || !ensureVariantId(form)) {
        window.alert('This product option is temporarily unavailable. Please refresh the page and try again.');
        return;
      }
      setPurchaseBusy(true);
      closeTechnique();
      if (cartBody) cartBody.innerHTML = '<div class="empty-bag"><p>Adding To Your Bag…</p></div>';
      await openBag({ refresh: false });
      let added = false;
      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'This item could not be added.');
        }
        added = true;
        await refreshCart();
      } catch (error) {
        if (error instanceof TypeError && !added) {
          closeBag();
          form.submit();
          return;
        }
        if (added && cartBody) {
          cartBody.innerHTML = `<div class="empty-bag"><p>Added To Your Bag.</p><a href="${rootPath()}cart">View Bag</a></div>`;
          return;
        }
        closeBag();
        window.alert(error instanceof Error ? error.message : 'This item could not be added.');
      } finally {
        setPurchaseBusy(false);
      }
    };

    forms.forEach((form) => form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submitter = event.submitter;
      if (submitter instanceof HTMLButtonElement && submitter.disabled) return;
      await submitProduct(form);
    }));
    root.querySelectorAll('[data-add-trigger]').forEach((button) => button.addEventListener('click', () => {
      if (!button.disabled) primaryForm?.requestSubmit();
    }));

    const reviewSource = root.querySelector('[data-dream-review-source]');
    const featuredReviews = root.querySelector('[data-dream-featured-reviews]');
    const reviewToggle = root.querySelector('[data-dream-reviews-toggle]');
    let reviewsExpanded = false;
    let reviewsInitialised = false;
    let reviewObserver = null;
    let reviewUpdateQueued = false;
    const normalise = (value) => String(value || '').replace(/\s+/g, ' ').trim();
    const makeFeaturedCard = (title, body, author) => {
      const card = document.createElement('article');
      card.className = 'review-card';
      card.innerHTML = `<div class="review-meta"><p class="stars" aria-label="5 out of 5 stars">★★★★★</p></div><h3>${escapeHtml(normalise(title))}</h3><blockquote>${escapeHtml(normalise(body))}</blockquote><footer><p class="review-author">${escapeHtml(normalise(author))}</p><p class="verified-customer">Verified Customer</p></footer>`;
      return card;
    };
    const addVerifiedLabels = () => {
      reviewSource?.querySelectorAll('.jdgm-rev').forEach((review) => {
        const reviewTitle = normalise(review.querySelector('.jdgm-rev__title')?.textContent).toLowerCase();
        const reviewAuthor = review.querySelector('.jdgm-rev__author');
        if (reviewTitle.includes('softest skin ever') && normalise(reviewAuthor?.textContent).toLowerCase().includes('zahra karim')) {
          reviewAuthor.textContent = 'Z.K.';
        }
        if (review.querySelector('.es-verified-customer')) return;
        const label = document.createElement('span');
        label.className = 'verified-customer es-verified-customer';
        label.textContent = 'Verified Customer';
        (review.querySelector('.jdgm-rev__author-wrapper') || review).append(label);
      });
    };
    const applyApprovedReviewOrder = () => {
      const reviews = [...(reviewSource?.querySelectorAll('.jdgm-rev') || [])];
      if (reviews.length < 6) return;
      const zK = reviews.find((review) => {
        const author = normalise(review.querySelector('.jdgm-rev__author')?.textContent).toLowerCase();
        const title = normalise(review.querySelector('.jdgm-rev__title')?.textContent).toLowerCase();
        return author === 'z.k.' || title.includes('softest skin ever');
      });
      const anya = reviews.find((review) => normalise(review.querySelector('.jdgm-rev__author')?.textContent).toLowerCase() === 'anya');
      if (!zK || !anya || zK.parentElement !== anya.parentElement) return;
      const parent = zK.parentElement;
      let current = [...parent.querySelectorAll(':scope > .jdgm-rev')];
      if (current[4] !== zK) parent.insertBefore(zK, current[4] || null);
      current = [...parent.querySelectorAll(':scope > .jdgm-rev')];
      if (current[5] !== anya) parent.insertBefore(anya, current[5] || null);
    };
    const populateFeaturedReviews = () => {
      if (!featuredReviews || featuredReviews.childElementCount) return;
      const desired = ['Charlotte W.', 'Olivia R.', 'Sophie M.'];
      const judgeReviews = [...(reviewSource?.querySelectorAll('.jdgm-rev') || [])];
      const ordered = desired.map((name) => judgeReviews.find((review) => normalise(review.querySelector('.jdgm-rev__author')?.textContent).includes(name))).filter(Boolean);
      (ordered.length === 3 ? ordered : judgeReviews.slice(0, 3)).forEach((review) => {
        featuredReviews.append(makeFeaturedCard(
          review.querySelector('.jdgm-rev__title')?.textContent,
          review.querySelector('.jdgm-rev__body')?.textContent,
          review.querySelector('.jdgm-rev__author')?.textContent
        ));
      });
    };
    const updateReviewVisibility = () => {
      reviewSource?.querySelectorAll('.jdgm-rev').forEach((review, index) => {
        review.hidden = !reviewsExpanded && index > 5;
      });
    };
    const runReviewEnhancements = () => {
      if (!reviewSource) return;
      reviewObserver?.disconnect();
      applyApprovedReviewOrder();
      addVerifiedLabels();
      populateFeaturedReviews();
      updateReviewVisibility();
      reviewObserver.observe(reviewSource, { childList: true, subtree: true });
    };
    const queueReviewEnhancements = () => {
      if (reviewUpdateQueued) return;
      reviewUpdateQueued = true;
      requestAnimationFrame(() => {
        reviewUpdateQueued = false;
        runReviewEnhancements();
      });
    };
    const initialiseReviews = () => {
      if (!reviewSource || reviewsInitialised) return;
      reviewsInitialised = true;
      reviewObserver = new MutationObserver(queueReviewEnhancements);
      runReviewEnhancements();
    };
    const reviewActivationTarget = featuredReviews?.closest('.featured-reviews') || reviewSource;
    if (reviewActivationTarget && 'IntersectionObserver' in window) {
      const reviewActivationObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        reviewActivationObserver.disconnect();
        initialiseReviews();
      }, { rootMargin: '200px 0px' });
      reviewActivationObserver.observe(reviewActivationTarget);
    } else if (reviewSource && 'requestIdleCallback' in window) {
      window.requestIdleCallback(initialiseReviews, { timeout: 2500 });
    } else if (reviewSource) {
      window.setTimeout(initialiseReviews, 250);
    }
    reviewToggle?.addEventListener('click', () => {
      initialiseReviews();
      reviewsExpanded = !reviewsExpanded;
      updateReviewVisibility();
      reviewToggle.setAttribute('aria-expanded', String(reviewsExpanded));
      reviewToggle.textContent = reviewsExpanded ? 'Show Fewer Reviews' : `Read All ${root.dataset.reviewCount || 0} Reviews`;
    });

    const stickyPurchase = root.querySelector('[data-sticky-purchase]');
    const finalPurchase = root.querySelector('[data-final-purchase]');
    if (stickyPurchase && primaryAddButton && 'IntersectionObserver' in window) {
      let primaryAboveViewport = false;
      let finalReached = false;
      const syncStickyPurchase = () => { stickyPurchase.hidden = !primaryAboveViewport || finalReached; };
      const stickyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const rootTop = entry.rootBounds?.top || 0;
          const rootBottom = entry.rootBounds?.bottom || window.innerHeight;
          if (entry.target === primaryAddButton) {
            primaryAboveViewport = !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
          } else if (entry.target === finalPurchase) {
            finalReached = entry.isIntersecting || entry.boundingClientRect.top < rootBottom;
          }
        });
        syncStickyPurchase();
      });
      stickyObserver.observe(primaryAddButton);
      if (finalPurchase) stickyObserver.observe(finalPurchase);
      syncStickyPurchase();
    } else if (stickyPurchase && primaryAddButton) {
      let stickyFrame = 0;
      const updateStickyPurchase = () => {
        stickyFrame = 0;
        const heroPassed = primaryAddButton.getBoundingClientRect().bottom < 0;
        const finalVisible = finalPurchase ? finalPurchase.getBoundingClientRect().top < window.innerHeight : false;
        stickyPurchase.hidden = !heroPassed || finalVisible;
      };
      const scheduleStickyPurchase = () => {
        if (!stickyFrame) stickyFrame = requestAnimationFrame(updateStickyPurchase);
      };
      scheduleStickyPurchase();
      document.addEventListener('scroll', scheduleStickyPurchase, { passive: true });
      window.addEventListener('resize', scheduleStickyPurchase, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting && !entry.target.closest('[hidden]')) {
          hydrateVideo(entry.target);
          entry.target.play().catch(() => undefined);
        } else entry.target.pause();
      }), { rootMargin: '320px 0px' });
      root.querySelectorAll('[data-es-deferred-video]').forEach((video) => videoObserver.observe(video));
    } else {
      root.querySelectorAll('[data-es-deferred-video]').forEach(hydrateVideo);
    }

    document.addEventListener('keydown', (event) => {
      const activeDialog = technique && !technique.hidden ? technique : drawer && !drawer.hidden ? drawer : null;
      if (event.key === 'Tab' && activeDialog) trapFocus(activeDialog, event);
      if (event.key === 'Escape') {
        if (technique && !technique.hidden) closeTechnique();
        else if (drawer && !drawer.hidden) closeBag();
      }
    });
  };

  const boot = () => document.querySelectorAll('[data-es-pdp]').forEach(initialise);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
